import React, { useEffect, useRef, useState } from 'react';
import { Send, RotateCcw } from 'lucide-react';
import { BlocoDoAssistente } from '../shared/BlocosDoAssistente';

/**
 * A aba do assistente: a página de abertura de quem tem ele ligado.
 *
 * Como o superIntelligent: a conversa ocupa a tela, as outras abas ficam em
 * cima. Os modais abrem POR CIMA desta página, e ao fechar a pessoa volta
 * para a conversa, onde a cadeia de próximo passo já está esperando.
 *
 * Só desenha. A conversa, o envio e o que cada botão faz moram no FIVSystem,
 * que é quem tem as funções que abrem os modais. Botão de mensagem velha
 * continua valendo — o serviço é procurado de novo no clique.
 */

// Ponto de partida para quem não sabe o que pedir. Clicar escreve o pedido;
// falta só completar com o cliente ou o número.
const IDEIAS = [
  'O que tem pra hoje?',
  'Pendências',
  'Receptoras prenhas',
  'Lista os serviços do ',
  'Lançar a TE do ',
  'Refaturar o serviço do ',
  'Emitir todas as entradas',
];

const Botao = ({ b, onAcao }) => (
  <button onClick={() => onAcao(b.acao)}
    className="px-3 py-1.5 rounded-lg border border-slate-300 bg-white text-slate-800 hover:bg-slate-50">
    {b.rotulo}
  </button>
);

/**
 * Uma mensagem: a fala, os botões soltos e o bloco (lista, cartão,
 * pendências). O bloco ocupa a largura toda — é elemento de tela, não balão.
 */
const Mensagem = ({ m, ctx, onAcao }) => (
  <div className={m.autor === 'pessoa' ? 'flex justify-end' : 'space-y-2'}>
    {m.texto && (
      <p className={`rounded-2xl px-4 py-2 whitespace-pre-line ${m.autor === 'pessoa'
        ? 'max-w-[85%] bg-blue-50 border border-blue-100 text-slate-800' : 'inline-block bg-white border border-slate-200 text-slate-800'}`}>
        {m.texto}
      </p>
    )}
    {m.botoes?.length > 0 && (
      <div className="flex flex-wrap gap-2">
        {m.botoes.map((b, j) => <Botao key={j} b={b} onAcao={onAcao} />)}
      </div>
    )}
    {m.bloco && <BlocoDoAssistente bloco={m.bloco} ctx={ctx} onAcao={onAcao} />}
  </div>
);

const AssistenteTab = ({ mensagens, pensando, onEnviar, onAcao, onNovaConversa, nomeDaPessoa, ctx = {} }) => {
  const [texto, setTexto] = useState('');
  const campo = useRef(null);
  const fim = useRef(null);

  useEffect(() => { fim.current?.scrollIntoView({ block: 'end' }); }, [mensagens, pensando]);

  const enviar = (e) => {
    e.preventDefault();
    const t = texto.trim();
    if (!t || pensando) return;
    setTexto('');
    onEnviar(t);
  };

  // Pedido completo vai direto; o que termina em espaço espera o nome.
  const comecar = (ideia) => {
    if (!ideia.endsWith(' ')) { onEnviar(ideia); return; }
    setTexto(ideia);
    campo.current?.focus();
  };

  return (
    <div className="max-w-5xl mx-auto flex flex-col min-h-[calc(100vh-12rem)]">
      <div className="flex-1 space-y-4 text-sm pb-4">
        {mensagens.length > 0 && (
          <div className="sticky top-0 z-10 flex justify-end bg-slate-50 py-1">
            <button onClick={onNovaConversa} title="Encerra esta conversa e começa outra"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-slate-600 hover:bg-slate-100">
              <RotateCcw className="w-4 h-4" /> Nova conversa
            </button>
          </div>
        )}
        {mensagens.length === 0 && (
          <div className="pt-10 sm:pt-16 text-center">
            <h2 className="text-xl font-semibold text-slate-800">
              {nomeDaPessoa ? `Olá, ${nomeDaPessoa}.` : 'Olá.'} O que você quer fazer?
            </h2>
            <p className="mt-1 text-slate-500">Diga com suas palavras. Eu te levo direto ao lugar certo.</p>
            <div className="mt-6 flex flex-wrap justify-center gap-2">
              {IDEIAS.map(ideia => (
                <button key={ideia} onClick={() => comecar(ideia)}
                  className="px-3 py-1.5 rounded-full border border-slate-300 bg-white text-slate-700 hover:bg-slate-50">
                  {ideia.trim()}{ideia.endsWith(' ') ? '…' : ''}
                </button>
              ))}
            </div>
          </div>
        )}

        {mensagens.map((m, i) => <Mensagem key={i} m={m} ctx={ctx} onAcao={onAcao} />)}
        {pensando && <p className="text-slate-400">…</p>}
        <div ref={fim} />
      </div>

      <form onSubmit={enviar}
        className="sticky bottom-0 bg-slate-50 pb-2 pt-2 flex items-center gap-2">
        <input ref={campo} value={texto} onChange={(e) => setTexto(e.target.value)}
          placeholder="Ex.: refaturar o serviço do Francisco"
          className="flex-1 px-4 py-3 border border-slate-300 rounded-xl text-base bg-white
            focus:outline-none focus:ring-2 focus:ring-slate-300" />
        <button type="submit" disabled={pensando || !texto.trim()}
          className="p-3 rounded-xl bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40" title="Enviar">
          <Send className="w-5 h-5" />
        </button>
      </form>
    </div>
  );
};

export default AssistenteTab;
